import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

export const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [usePinLogin, setUsePinLogin] = useState(false)
  const [pin, setPin] = useState('')
  const { login, loginWithPin, loading, isAuthenticated, sessionExpired, clearSessionExpired } = useAuth()
  const navigate = useNavigate()

  React.useEffect(() => {
    if (sessionExpired) {
      const t = setTimeout(() => clearSessionExpired(), 8000)
      return () => clearTimeout(t)
    }
  }, [sessionExpired, clearSessionExpired])

  // Password validation
  const validatePassword = (pwd: string): { isValid: boolean; message: string } => {
    if (pwd.length < 8) {
      return { isValid: false, message: 'Password must be at least 8 characters' }
    }
    
    // Check for at least one uppercase letter
    if (!/[A-Z]/.test(pwd)) {
      return { isValid: false, message: 'Password must contain at least one uppercase letter' }
    }
    
    // Check for at least one lowercase letter
    if (!/[a-z]/.test(pwd)) {
      return { isValid: false, message: 'Password must contain at least one lowercase letter' }
    }
    
    // Check for at least one number
    if (!/\d/.test(pwd)) {
      return { isValid: false, message: 'Password must contain at least one number' }
    }
    
    // Check for at least one special character
    if (!/[!@#$%^&*()_+\-=\[\]{}|;:'",.<>?]/.test(pwd)) {
      return { isValid: false, message: 'Password must contain at least one special character' }
    }
    
    return { isValid: true, message: '' }
  }

  // Redirect if already authenticated
  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true })
    }
  }, [isAuthenticated, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (usePinLogin) {
      const pinClean = pin.replace(/\D/g, '')
      if (pinClean.length < 4 || pinClean.length > 6) {
        setError('PIN must be 4–6 digits')
        return
      }
      const result = await loginWithPin(email, pinClean)
      if (result.success) {
        setTimeout(() => navigate('/dashboard', { replace: true }), 100)
      } else {
        setError(result.message)
      }
      return
    }

    const passwordValidation = validatePassword(password)
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message)
      return
    }

    const result = await login(email, password)
    if (result.success) {
      setTimeout(() => navigate('/dashboard', { replace: true }), 100)
    } else {
      setError(result.message)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 to-purple-900">
      <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Sachin Bookshop</h1>
          <p className="text-white/70">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Enter your email"
              required
            />
          </div>

          {usePinLogin ? (
            <div>
              <label className="block text-sm font-medium text-white mb-2">PIN (4–6 digits)</label>
              <input
                type="password"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400"
                placeholder="Enter PIN"
                required
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-white mb-2">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-400 pr-10"
                  placeholder="Enter your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-white/70 hover:text-white"
                >
                  {showPassword ? '👁️' : '👁️'}
                </button>
              </div>
            </div>
          )}

          {sessionExpired && (
            <div className="bg-amber-500/20 border border-amber-500/30 text-amber-200 px-4 py-3 rounded-lg mb-4">
              Your session expired due to inactivity. Please sign in again.
            </div>
          )}
          {error && (
            <div className="bg-red-500/20 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold py-3 px-4 rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Signing In...' : usePinLogin ? 'Sign In with PIN' : 'Sign In'}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => { setUsePinLogin(!usePinLogin); setError(''); setPin(''); }}
            className="text-sm text-blue-400 hover:text-blue-300"
          >
            {usePinLogin ? 'Use password instead' : 'Quick login with PIN'}
          </button>
        </div>

        <div className="mt-6 text-center">
          <div className="flex justify-center space-x-4 text-sm">
            <Link to="/auth/register" className="text-blue-400 hover:text-blue-300">
              Create Account
            </Link>
            <span className="text-white/50">•</span>
            <Link to="/auth/forgot-password" className="text-blue-400 hover:text-blue-300">
              Forgot Password?
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}